import React, { ReactElement } from 'react';
import { Component } from "puzzle";
import KendoGridAbstract from "../../../util/KendoGridAbstract/KendoGridAbstract";
import { GRID_DATA_ITEM, NEGOTIATION_ACTION_NAME, NEGOTIATION_GRID_PROPS, NEGOTIATION_GRID_STATE } from "./Types";
import { GridColumn as Column } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import { FINANCIAL_DOCUMENT, OFFER_TASK, RATE, RATE_NAME } from "../Types";
import { isEqual, find, findIndex, cloneDeep } from "lodash";
import { GridCellProps, GridRowProps } from "@progress/kendo-react-grid";
import { getTableTdAccessibilityProps } from "../../../util/Util";
import {
  NumericTextBox,
  NumericTextBoxBlurEvent,
  NumericTextBoxChangeEvent
} from '@progress/kendo-react-inputs';
import Big from "big.js";
import classNames from "classnames";

class NegotiationGrid extends Component<NEGOTIATION_GRID_PROPS, NEGOTIATION_GRID_STATE> {
  public state: NEGOTIATION_GRID_STATE = {
    columns: [],
    editableFields: [],
    gridData: []
  }

  componentDidUpdate = (prevProps: NEGOTIATION_GRID_PROPS) => {
    let { actionName, selectedTask } = this.props;
    if (!isEqual(prevProps.selectedTask, selectedTask) || prevProps.actionName !== actionName) {
      this.prepareInitialData(selectedTask, actionName);
    }
  }

  /**
   * Calculate total rates using Big.js
   * @param gridData
   */
  calculateTotal = (gridData: GRID_DATA_ITEM[]) => {
    const { roundingDigits } = this.props;
    const { unitOfWork } = this.props.selectedTask;
    let counterOffer: GRID_DATA_ITEM = gridData[gridData.length - 1];
    let total = new Big(0);
    if (unitOfWork === 'WORD') {
      let requestOffer: GRID_DATA_ITEM = gridData[0];
      let current;
      let counterBig;
      for (const requestOfferKey in requestOffer) {
        if (requestOfferKey !== 'leftHeader' && requestOfferKey !== 'total') {
          current = new Big(requestOffer[requestOfferKey] || 0);
          counterBig = new Big(counterOffer[requestOfferKey] || 0);
          total = total.plus(current.mul(counterBig));
        }
      }
    } else {
      let { quantity, rate } = counterOffer;
      let bigQuantity = new Big(quantity || 0);
      let bigRate = new Big(rate || 0);
      total = bigQuantity.mul(bigRate);
    }
    counterOffer.total = total.round(roundingDigits).toNumber();
    gridData[gridData.length - 1] = counterOffer;

    return gridData;
  }

  /**
   * Column renderer
   * @param column
   */
  columnRenderer = (column: any) => {
    let title = column.field === 'leftHeader' ? ' ' : this.translate(`columnsHeader.${column.field}`);
    let customCellRenderer: any = this.CUSTOM_CELL_RENDERER[column.field];
    let gridColumn = <Column
      cell={customCellRenderer ? customCellRenderer : this.defaultCellRenderer}
      headerClassName={'k-text-right'}
      field={column.field}
      key={column.field}
      title={title}
    />

    return gridColumn;
  }

  /**
   * Default cell renderer, show input for edit cells
   *
   * @param cellProps
   */
  defaultCellRenderer = (cellProps: GridCellProps) => {
    let { dataItem, field } = cellProps;
    let { editableFields } = this.state;
    let { roundingDigits } = this.props;
    const tdProps = getTableTdAccessibilityProps(cellProps);
    let value;
    let inEdit;

    if (field) {
      value = dataItem[field];
      inEdit = editableFields.includes(field) && dataItem.status === 'COUNTERED';
    }

    return <td
      className={`${cellProps.className} k-text-right`}
      {...tdProps}
    >
      {inEdit ? (
        <NumericTextBox
          format={`n${this.props.roundingDigits}`}
          min={0}
          onBlur={this.numericFieldOnBlur.bind(this, (field as RATE_NAME))}
          onChange={this.numericFieldOnChange.bind(this, (field as RATE_NAME))}
          size={'small'}
          spinners={false}
          value={value}
        />
      ) : <span>{dataItem.status ? value?.toFixed(roundingDigits) : value}</span>}
    </td>;
  }

  /**
   * Left header cell renderer, status and user type
   * @param cellProps
   */
  leftHeaderCellRenderer = (cellProps: GridCellProps): ReactElement<HTMLTableCellElement> => {
    const { dataItem, field } = cellProps;
    const tdProps = getTableTdAccessibilityProps(cellProps);
    let value = dataItem[field || ''];
    let user;
    if (!value) {
      let status = dataItem.status;
      value = this.translate(`leftHeader.status.${status}`)
      user = this.translate(`leftHeader.user.${status}`)
    }
    return (
      <th {...tdProps} className={`${cellProps.className} k-text-right`} scope="row" role="rowheader">
        <div className={'tp-text-ellipsis'}>{value}</div>
        {user && <div className={'tp-color-accent tp-font-size-sm tp-text-ellipsis'}>{user}</div>}
      </th>
    );
  }

  /**
   * On blur error handler for numeric input field
   * Save edited rates
   * @param field
   * @param event
   */
  numericFieldOnBlur = (field: RATE_NAME, event: NumericTextBoxBlurEvent) => {
    let value: number = event.target.value || 0;
    if (value > 0) {
      let { budgetRatesUpdated, selectedTask } = this.props;
      const { gridData } = this.state;

      let budgetRates = selectedTask.budgetRates || [];
      if (selectedTask.status === 'COUNTERED') {
        //when supplier try to re-negotiate, then we need to use budgetRates from last financialDocument
        let financialDetails = selectedTask.financialDetails;
        budgetRates = financialDetails[financialDetails.length - 1].rates.map((item: RATE) => {
          return {
            budgetRateName: item.budgetRateNameEnum.name,
            rate: item.pricePerUnit
          }
        });
      }

      let index = findIndex(budgetRates, { budgetRateName: field });
      if (index !== -1) {
        budgetRates[index].rate = value;
      } else {
        budgetRates.push({
          budgetRateName: field,
          rate: value
        })
      }

      let counterOffer: GRID_DATA_ITEM = gridData[gridData.length - 1];
      budgetRatesUpdated({
        rates: budgetRates,
        total: counterOffer.total
      });
    }
  }

  /**
   * Numeric field onChange handler
   * Calculate total and call setState
   * @param field
   * @param event
   */
  numericFieldOnChange = (field: RATE_NAME, event: NumericTextBoxChangeEvent) => {
    let value = event.target.value;
    if (value && value > 0) {
      let { gridData } = this.state;
      gridData[gridData.length - 1][field] = value;
      gridData = this.calculateTotal(gridData);
      this.setState({ gridData });
    }
  }

  /**
   * Prepare columns config
   * @param data
   */
  prepareColumnsConfig = (data: OFFER_TASK) => {
    let columns: any[] = [];
    let requestDocument: FINANCIAL_DOCUMENT = data.financialDetails[0];
    columns.push({ field: 'leftHeader' });
    let rates = requestDocument.rates;
    let ratesLength = rates.length;
    let i = 0;
    let current;
    let rateName;
    while (i < ratesLength) {
      current = rates[i];
      rateName = current.budgetRateNameEnum.name;
      if (rateName !== 'RATE') {
        columns.push({
          field: rateName
        });
      }
      i++;
    }

    //additional columns
    let unitOfWork = data.unitOfWork;
    if (unitOfWork === 'WORD') {
      columns.push({ field: 'total' });
    } else {
      columns.push({ field: 'totalWords' }, { field: 'quantity' }, { field: 'rate' }, { field: 'total' });
    }

    return columns;
  }

  /**
   * Prepare and parse grid data
   * @param data
   */
  prepareGridData = (data: OFFER_TASK) => {
    let unitOfWork = data.unitOfWork;
    let financialDetails: FINANCIAL_DOCUMENT[] = data.financialDetails;
    //create first dataItem with data from REQUESTED financialDocument
    let firstDocument: RATE[] = financialDetails[0].rates;
    let requestedData: GRID_DATA_ITEM = {
      leftHeader: this.translate(`columnsHeader.wordCount`)
    };
    let totalWords = 0
    firstDocument.map((item: RATE) => {
      if (item.budgetRateNameEnum.name !== 'RATE') {
        requestedData[item.budgetRateNameEnum.name] = item.quantity;
        totalWords += item.quantity;
      }
    });
    let totalWordsField = unitOfWork === 'WORD' ? 'total' : 'totalWords';
    requestedData[totalWordsField] = totalWords
    let gridData: GRID_DATA_ITEM[] = [requestedData];
    let i = 0;
    let current;
    let rates: RATE[];
    let haveCounterOffer = false; //status COUNTERED
    let statusesForChange = ['APPROVED', 'ACCEPTED', 'PO_REQUESTED', 'PO_SENT', 'INVOICING_AVAILABLE', 'FINALIZED'];

    while (i < financialDetails.length) {
      current = financialDetails[i];

      if (statusesForChange.includes(current.status)) {
        //statusesForChange are statuses that need to be changed to COUNTERED
        current.status = 'COUNTERED';
      }
      let dataItem: any = {
        //when pm edit budget then instead of PROVIDED (first financialDetails item) we get UPDATED status
        status: current.status === 'UPDATED' && gridData.length === 1 ? 'PROVIDED' : current.status
      };
      if (!haveCounterOffer) {
        haveCounterOffer = current.status === 'COUNTERED';
      }
      rates = current.rates;
      rates.map((item: RATE) => {
        if (item.pricePerUnit) {
          if (item.budgetRateNameEnum.name !== 'RATE') {
            dataItem[item.budgetRateNameEnum.name] = item.pricePerUnit;
          }
        }
      });
      let mainRate = find(rates, (value: RATE) => value.budgetRateNameEnum.name === 'RATE');
      if (unitOfWork !== 'WORD') {
        dataItem = {
          ...dataItem,
          quantity: mainRate?.quantity,
          rate: mainRate?.pricePerUnit,
          total: mainRate?.total
        }
      } else {
        dataItem = {
          ...dataItem,
          total: current.total
        }
      }
      gridData.push(dataItem);
      i++;
    }
    if (!haveCounterOffer) {
      let counterOfferData = cloneDeep(gridData[1]); //take requested dataItem, always on index 1
      if (unitOfWork === 'FLAT') {
        counterOfferData = {
          quantity: counterOfferData.quantity,
          rate: counterOfferData.rate,
          total: counterOfferData.total
        }
      }

      gridData.push({
        ...counterOfferData,
        status: 'COUNTERED',
        leftHeader: null
      });
    }

    let counterOfferItem = gridData[gridData.length - 1];
    let budgetRates = data.budgetRates || [];
    i = 0;
    current = null;

    while (i < budgetRates.length) {
      current = budgetRates[i];
      counterOfferItem[current.budgetRateName] = current.rate;
      i++;
    }
    gridData[gridData.length - 1] = counterOfferItem;

    gridData = this.calculateTotal(gridData);

    return gridData;
  }

  /**
   * Prepare initial data
   * Parse grid and columns data
   * @param data
   * @param actionName
   */
  prepareInitialData = (data: OFFER_TASK, actionName: NEGOTIATION_ACTION_NAME) => {
    let gridData: any = this.prepareGridData(data);
    let columns: any = this.prepareColumnsConfig(data);

    let editableFields: string[] = [];
    if (actionName === 'negotiate') {
      let { unitOfWork } = data;
      if (unitOfWork === 'WORD') {
        editableFields = data.financialDetails[0].rates.map((item: RATE) => item.budgetRateNameEnum.name);
      } else {
        editableFields = ['rate'];
      }
    }
    this.setState({
      columns,
      editableFields,
      gridData
    });
  }

  /**
   * Quantity column cell renderer
   * Add tp-color-accent class and preserve all other props
   * @param cellProps
   */
  quantityCellRenderer = (cellProps: GridCellProps): ReactElement<HTMLTableCellElement> => {
    const { dataItem, field } = cellProps;
    const tdProps = getTableTdAccessibilityProps(cellProps);
    let value = dataItem[field || ''];

    return (
      <td {...tdProps} className={`${cellProps.className} k-text-right`}>
        <span className={'tp-color-accent'}>{value}</span>
      </td>
    );
  }

  /**
   * Row renderer
   * @param trElement
   * @param rowProps
   */
  rowRender = (trElement: ReactElement<HTMLTableRowElement>, rowProps: GridRowProps) => {
    const elementProps = { ...trElement.props };
    const status = rowProps.dataItem.status;

    if (status) {
      elementProps.className += ' pd-negotiation-grid-row-grey';
    }

    return React.cloneElement(trElement, { ...elementProps }, elementProps.children);
  }

  renderBody() {
    const { columns, gridData } = this.state;
    const { selectedTask } = this.props;
    const { status, unitOfWork } = selectedTask;
    let className = classNames({
      'pd-offer-word-grid': unitOfWork === 'WORD',
      'pd-offer-grid-updated': status === 'UPDATED' //case when project-manager send counter-offer (additional row)
    });
    return (
      <KendoGridAbstract
        className={className}
        data={gridData}
        rowRender={this.rowRender}
      >
        {columns.map(this.columnRenderer)}
      </KendoGridAbstract>
    );
  }

  CUSTOM_CELL_RENDERER: any = {
    leftHeader: this.leftHeaderCellRenderer,
    quantity: this.quantityCellRenderer
  }
}


export default NegotiationGrid;
